<template>
  <div>
    <loader :isShow="isLoading" :textInfo="''" />
    <div v-if="!isLoading">
      <div @click="cancel" class="vm--overlay"></div>
      <div class="create-rental-container">
        <div class="create-rental-title">{{ titleText }}</div>

        <div class="create-rental-schedule-error" v-if="showErrorMessage">
          <img src="../../assets/error_red.png" />
          <span>{{ errorMessage }}</span>
        </div>

        <div class="create-rental-basic-info-container">
          <div class="create-rental-label-input">
            <label for="full-name">Housemate:</label>
            <CustomSelect
              :options="allHousemates"
              :default="name ? name : 'Select Housemate'"
              class="select"
              :class="{ error: !validHousemate }"
              @input="onSelectHousemate"
            >
              <div slot="custom-render" class="add-new-housemate">
                <img class="add-icon" src="../../assets/add_circle.svg" />
                <span @click="showSaveHousemate">Create new housemate</span>
              </div>
            </CustomSelect>
          </div>
          <div class="create-rental-label-input">
            <label for="number-housemates">Number of Housemates</label>
            <input
              :class="{ error: !validNumHousemates }"
              v-model="numHousemates"
              placeholder="E.g. 1"
              type="number"
              min="1"
              id="number-housemates"
            />
          </div>
        </div>

        <div class="create-rental-dropdowns-container">
          <div class="create-rental-label-dropdown">
            <label for="property-dropdown">Property</label>
            <select
              :class="{ error: !validProperty }"
              v-model="property"
              name="property-dropdown"
              id="property"
            >
              <option selected :value="null">Select Property</option>
              <option
                v-for="property in properties"
                :value="property"
                :key="'property-' + property.uuid"
              >
                {{ property.name }}
              </option>
            </select>
          </div>
          <div class="create-rental-label-dropdown">
            <label for="room-dropdown">Room</label>
            <select
              :class="{ error: !validRoom }"
              v-model="room"
              name="room-dropdown"
              id="room"
            >
              <option selected :value="null">Select Room</option>
              <option
                v-for="room in rooms"
                :value="{ uuid: room.uuid }"
                :key="'room-' + room.uuid"
              >
                {{ room.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="create-rental-details-container">
          <div class="create-rental-label-dropdown">
            <label for="arrival">Arrival</label>
            <div
              class="create-rental-date-picker-wrapper"
              :class="{ error: !validArrival }"
            >
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="Select Date"
                v-model="arrival"
                name="arrival"
              />
            </div>
          </div>
          <div class="create-rental-label-dropdown">
            <label for="departure">Departure</label>
            <div
              class="create-rental-date-picker-wrapper"
              :class="{ error: !validDeparture }"
            >
              <DatePicker
                @click="selectingDeparture = !selectingDeparture"
                :class="{ error: !validDeparture }"
                format="YYYY-MM-DD"
                v-model="departure"
                placeholder="Select Date"
                name="departure"
              />
            </div>
          </div>

          <div class="create-rental-label-dropdown">
            <label for="Channel">Channel</label>
            <select
              :class="{ error: !validChannel }"
              v-model="channel"
              name="Channel"
              id="Channel"
            >
              <option selected :value="null">Select Channel</option>
              <option
                v-for="option in ['Airbnb', 'Booking']"
                :value="option"
                :key="option"
              >
                {{ option }}
              </option>
            </select>
          </div>
          <div class="create-rental-label-input">
            <label for="confirmation-code">Confirmation Code</label>
            <input
              :class="{ error: !validConfirmationCode }"
              :disabled="isUpdate"
              v-model="confirmationCode"
              placeholder="E.g. ABDCD/1234"
              type="text"
              name="confirmation-code"
              id="confirmation-code"
            />
          </div>
        </div>

        <div class="create-rental-buttons-container">
          <button @click="cancel()" class="create-rental-cancel-button">
            Cancel
          </button>
          <button @click="submit()" class="create-rental-create-button">
            {{ buttonText }}
          </button>
        </div>
      </div>
      <div v-if="isShowSaveHousemate">
        <SaveHousemateModal @close="hideSaveHousemate" @ok="saveHousemate" />
      </div>
    </div>
  </div>
</template>

<script>
import calls from '@/api/calls.js'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import '../../assets/css/vue2-datepicker.css'
import CustomSelect from '@/components/shared/select/CustomSelect.vue'
import SaveHousemateModal from './SaveHousemateModal.vue'
import Loader from '@/components/Loader.vue'
import {
  required,
  dateAfter,
  dateAfterToday,
  validateFields,
  ERRORS
} from '@/functions/validation.js'

const logError = (title, error) => {
  console.error(`${title}: ${error.message}`)
}

export default {
  name: 'NewRentalScreen',
  components: {
    DatePicker,
    CustomSelect,
    SaveHousemateModal,
    Loader
  },
  props: {
    selectedRental: null
  },
  data() {
    return {
      name: null,
      numHousemates: null,
      phone: null,
      email: null,
      arrival: null,
      departure: null,
      channel: null,
      confirmationCode: null,
      properties: [],
      allRooms: [],
      rooms: [],
      property: null,
      room: null,
      housemate: null,
      originalRental: null,
      rental: null,
      isUpdate: false,
      titleText: null,
      buttonText: null,
      allHousemates: [],
      isShowSaveHousemate: false,

      //validators
      showErrorMessage: false,
      validName: true,
      validEmail: true,
      validPhone: true,
      validHousemate: true,
      validNumHousemates: true,
      validProperty: true,
      validRoom: true,
      validArrival: true,
      validDeparture: true,
      validConfirmationCode: true,
      validChannel: true,
      errorMessage: '',
      isLoading: false
    }
  },
  methods: {
    cancel() {
      this.$emit('cancel')
    },
    validateRental() {
      this.validNumHousemates = required(this.numHousemates)
      this.validHousemate = required(this.phone)
      this.validProperty = required(this.property)
      this.validRoom = required(this.room)
      this.validArrival = required(this.arrival)
      this.validDeparture = required(this.departure)
      this.validConfirmationCode = required(this.confirmationCode)
      this.validChannel = required(this.channel)

      const { valid } = validateFields(
        [
          'numHousemates',
          'property',
          'room',
          'arrival',
          'departure',
          'confirmationCode',
          'channel'
        ],
        this
      )
      if (!valid) this.errorMessage = ERRORS.REQUIRED_FIELDS
      return valid
    },
    validateSchedule() {
      const missingArrival = !required(this.arrival)
      const missingDeparture = !required(this.departure)
      const invalidSchedule =
        !missingArrival &&
        !missingDeparture &&
        !dateAfter(this.arrival, this.departure)
      const invalidDeparture =
        !missingDeparture && !dateAfterToday(this.departure)

      if (missingArrival || missingDeparture) {
        this.errorMessage = ERRORS.REQUIRED_FIELDS
      } else if (invalidSchedule) {
        this.errorMessage = ERRORS.DEPARTURE_AFTER_ARRIVAL
      } else if (invalidDeparture) {
        this.errorMessage = ERRORS.DEPARTURE_AFTER_TODAY
      }

      this.validArrival = !invalidSchedule && !missingArrival
      this.validDeparture =
        !invalidSchedule && !missingDeparture && !invalidDeparture

      return !(
        invalidSchedule ||
        missingArrival ||
        missingDeparture ||
        invalidDeparture
      )
    },
    async deleteHousemate() {
      const { message, data } = await calls.DELETE(
        'housemates',
        this.housemate.uuid
      )
      if (message) throw Error(message)
      return data
    },
    async createHousemate() {
      const { message, data } = await calls.POST('housemates', {
        name: this.name,
        email: this.email,
        phone: this.phone
      })
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async createRental() {
      const { message, data } = await calls.POST(
        `rental/${this.room.uuid}/${this.housemate.uuid}`,
        {
          arrival: moment(this.arrival)
            .utcOffset(this.property.utcoffset)
            .toISOString(),
          departure: moment(this.departure)
            .utcOffset(this.property.utcoffset)
            .toISOString(),
          confirmationCode: this.confirmationCode,
          houseCode: 1234,
          channel: this.channel,
          companions: this.numHousemates
        }
      )
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async findHousemate() {
      const { message, data } = await calls.GET(
        'housemates/by/phone',
        this.phone
      )
      if (message) throw Error(message)
      return data
    },
    async findRental() {
      const { message, data } = await calls.GET(
        'rental/a',
        `${this.room.uuid}/${this.housemate.uuid}`
      )
      if (message) throw Error(message)
      return data
    },
    async getAllRentals() {
      const { message, data } = await calls.GET(
        'rental/housemates',
        this.room.uuid
      )
      if (message) throw Error(message)
      return data
    },
    async updateHousemate() {
      const { message, data } = await calls.PUT(
        'housemates',
        this.housemate.uuid,
        {
          name: this.name,
          email: this.email,
          phone: this.phone
        }
      )
      if (message) {
        this.$notify({
          group: 'rent-mgt-notification',
          type: 'error',
          text: message
        })
      } else {
        return data
      }
    },
    async updateRental() {
      {
        const { message } = await calls.DELETE(
          `rental/${this.confirmationCode}`
        )
      }
      {
        //i know this is repeated code but it for easier debugging and error handling, create a new rental with the new room and stuff
        const { message, data } = await calls.POST(
          `rental/${this.room.uuid}/${this.housemate.uuid}`,
          {
            arrival: moment(this.arrival)
              .utcOffset(this.property.utcoffset)
              .toISOString(),
            departure: moment(this.departure)
              .utcOffset(this.property.utcoffset)
              .toISOString(),
            confirmationCode: this.confirmationCode,
            houseCode: 1234,
            channel: this.channel,
            companions: this.numHousemates
          }
        )
        if (message) {
          this.$notify({
            group: 'rent-mgt-notification',
            type: 'error',
            text: message
          })
        } else {
          return data
        }
      }
    },
    async loadAllData() {
      const { message, data } = await calls.GET('properties/with-rooms/all')
      this.properties = data.properties
      this.allRooms = data.rooms

      let houseMates = data.housemates.map((housemate) => {
        return {
          ...housemate,
          label: housemate.name,
          value: housemate.uuid
        }
      })
      this.allHousemates = houseMates
    },
    async toggleEditMode() {
      this.titleText = 'Edit Rental'
      this.buttonText = 'Save Changes'
      this.isUpdate = true
      const room = this.rental[1]
      const rental = this.rental[2]
      const housemate = this.rental[3]
      this.name = housemate.name
      this.email = housemate.email
      this.phone = housemate.phone
      this.housemate = housemate

      const roomProperty = this.allRooms.find((e) => e.uuid == room.uuid)
      this.property = this.properties.find(
        (e) => e.uuid == roomProperty.propertyId
      )

      this.room = {
        uuid: room.uuid
      }
      this.arrival = rental.arrivalOriginal ? new Date(rental.arrival) : null
      this.departure = rental.departureOriginal
        ? new Date(rental.departure)
        : null
      this.confirmationCode = rental.confirmationCode
      this.channel =
        rental.channel == 'airbnb_official' ? 'Airbnb' : rental.channel
      this.numHousemates = rental.companions
    },
    async submit() {
      const validRental = this.validateRental()
      const validSchedule = this.validateSchedule()
      const allValid = validRental && validSchedule
      this.showErrorMessage = allValid ? false : true
      if (allValid) {
        //check if that housemate existst before updating
        let found_housemate = null
        try {
          found_housemate = await this.findHousemate()
        } catch (e) {
          logError('findHousemate', e)
          return
        }

        //check if thaat room is available
        let room_available = true
        let other_housemate = null
        let all_rentals = null
        try {
          all_rentals = await this.getAllRentals()
        } catch (e) {
          logError('getAllRentals', e)
        }
        if (all_rentals != null) {
          for (let rental of all_rentals) {
            if (
              found_housemate != null &&
              rental[0].uuid == found_housemate.uuid &&
              this.isUpdate
            )
              //updating a housemate's rental
              continue
            if (
              moment(this.arrival).isBefore(
                moment(rental[1].departure, 'YYYY-MM-DDTHH:mm:ss')
              )
            ) {
              //cant create aa new rental if its occupied
              room_available = false
              other_housemate = rental[0].name
              break
            }
          }
        }

        if (room_available) {
          if (this.isUpdate) {
            if (!found_housemate) {
              return
            }
            //update housemate
            {
              this.housemate = found_housemate
              try {
                await this.updateHousemate()
              } catch (e) {
                logError('updateRental', e)
                return
              }
            }
            //update rental
            {
              try {
                await this.updateRental()
              } catch (e) {
                logError('updateRental', e)
                return
              }
            }
          } else {
            //create rental
            try {
              await this.createRental()
            } catch (e) {
              logError('createRental', e)
              return
            }
          }
        } else {
          this.errorMessage = 'An existing rental overlaps with dates'
          this.showErrorMessage = true
          this.validArrival = false
          this.validDeparture = false
          return
        }

        this.$emit('submitted')
      }
    },
    onSelectHousemate(payload) {
      this.name = payload.name
      this.phone = payload.phone
      this.email = payload.email
      this.housemate = payload
    },
    showSaveHousemate() {
      this.isShowSaveHousemate = true
    },
    hideSaveHousemate() {
      this.isShowSaveHousemate = false
    },
    async saveHousemate(payload) {
      this.name = payload.housemateFullName
      this.phone = payload.housematePhoneNumber
      this.email = payload.housemateEmail
      // find housemate by phone
      let housemate = await this.findHousemate()
      if (housemate != null) {
        this.housemate = housemate
        await this.updateHousemate()
      } else {
        await this.createHousemate()
      }
      this.isShowSaveHousemate = false
    }
  },
  asyncComputed: {
    async rooms() {
      if (this.property) {
        await this.$nextTick()
        return this.allRooms.filter(
          (room) => room.propertyId == this.property.uuid
        )
      } else {
        return []
      }
    }
  },
  async mounted() {
    this.isLoading = true
    await this.loadAllData()
    this.titleText = 'New Rental'
    this.buttonText = 'Create Rental'
    if (this.selectedRental != null) {
      this.rental = this.selectedRental
      this.originalRental = this.selectedRental
      await this.toggleEditMode()
    }
    this.isLoading = false
  }
}
</script>

<style scoped>
.vm--overlay {
  z-index: 40;
}
</style>

<style>
.create-rental-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 40;
}

.create-rental-container {
  background-color: white;
  position: absolute;
  width: 472px;
  top: 10%;
  left: calc(50% - (780px * 0.25));
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  margin: 0;
  padding: 30px 24px;
  z-index: 41;
  border-radius: 4px;
}

.create-rental-container .mx-datepicker .mx-input {
  cursor: pointer;
  font-family: 'Montserrat';
  color: #2c3e50;
}

.create-rental-container .mx-datepicker .mx-input::placeholder {
  color: #2c3e50;
  opacity: 1;
}

.create-rental-container .mx-datepicker .mx-input::-ms-input-placeholder {
  color: #2c3e50;
}

.create-rental-title {
  font-family: Montserrat;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: #66b015;
  margin-bottom: 20px;
}

.create-rental-basic-info-container {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
}

.create-rental-label-input {
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  height: 64px;
  width: 213px;
  padding: 12px 0 12px 0;
}

.create-rental-label-input label {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  color: #4a4a4a;
}
.create-rental-label-input input {
  width: 212px;
  height: 40px;
  margin-top: 5px;
  border: 1px solid #cbd4dc;
  box-sizing: border-box;
  border-radius: 5px;
  padding: 0 8px 0 8px;
  font-size: 13px;
}

.create-rental-dropdowns-container {
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.create-rental-label-dropdown {
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  padding: 12px 0 12px 0;
  width: 100%;
  height: 64px;
}

.create-rental-label-dropdown label {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 18px;
  color: #4a4a4a;
}

.create-rental-label-dropdown select {
  margin-top: 5px;
}

.create-rental-details-container {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  position: relative;
}
.create-rental-details-container .create-rental-label-dropdown {
  width: 212px;
}

.create-rental-buttons-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: baseline;
  padding-top: 10px;
  width: 100%;
}

.create-rental-cancel-button,
.create-rental-create-button {
  font-family: Montserrat;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: left;
  border-radius: 4px;
  width: 176px;
  height: 48px;
  text-align: center;
}

.create-rental-cancel-button:hover,
.create-rental-create-button:hover {
  cursor: pointer;
}

.create-rental-cancel-button {
  border: 1px solid #767675;
  background: #ffffff;
  color: #767675;
}

.create-rental-create-button {
  border: none;
  background: #66b015;
  color: white;
  margin-left: 24px;
}

input.error,
select.error {
  border-color: red;
}

.create-rental-date-picker-wrapper {
  margin-top: 5px;
  border-radius: 6px;
  border: 1px solid white;
}

.create-rental-schedule-error {
  display: flex;
  flex-flow: row nowrap;
  padding: 12px;
  width: 100%;
  color: red;
  background: rgba(223, 0, 15, 0.05);
  border-radius: 6px;
}

.create-rental-schedule-error span {
  margin-left: 12px;
  color: #df000f;
  font-family: Montserrat;
  font-weight: 900;
  font-size: 13px;
  line-height: 18px;
}

div.mx-datepicker {
  border: none;
  box-sizing: border-box;
  border-radius: 5px;
  width: 100%;
  height: 40px;
  cursor: pointer;
}
.custom-select .add-new-housemate {
  border: 1px solid #66b015;
  border-radius: 4px;
  margin: 5px 8px 5px 8px;
  color: #66b015;
  font-weight: 600;
  text-align: center;
  position: relative;
  border-bottom: 1px solid #66b015;
  cursor: pointer;
  padding-left: 17px;
  font-size: 11px;
  height: 40px;
  line-height: 40px;
}

.custom-select .add-new-housemate .add-icon {
  left: 10px;
  top: 10px;
  position: absolute;
}
</style>
